import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Brain } from 'lucide-react';
import { vdAIConfigApi } from '../api';

export default function VDAIService() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { register, handleSubmit, reset, watch, setValue } = useForm();
  const enabled = watch('enabled');

  useEffect(() => { vdAIConfigApi.get().then(d => { reset(d); setLoading(false); }); }, [reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    await vdAIConfigApi.update(data);
    toast.success('AI settings saved');
    setSaving(false);
  };

  if (loading) return <div className="animate-pulse h-96 bg-card/5 rounded-xl" />;

  return (
    <div className="max-w-2xl space-y-6" data-testid="vd-ai-service">
      <div>
        <h1 className="text-2xl font-bold">AI Customer Service</h1>
        <p className="text-muted-foreground text-sm mt-1">Configure the AI brain that handles customer calls</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Card className="bg-card/5 border-white/10">
          <CardContent className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3">
              <Brain className={`w-5 h-5 ${enabled ? 'text-emerald-400' : 'text-muted-foreground'}`} />
              <div>
                <div className="font-medium text-sm">AI Customer Service</div>
                <div className="text-xs text-muted-foreground">{enabled ? 'AI will handle incoming calls' : 'AI is disabled — calls route directly'}</div>
              </div>
            </div>
            <Switch checked={!!enabled} onCheckedChange={v => setValue('enabled', v)} />
          </CardContent>
        </Card>

        {enabled && (
          <>
            <Card className="bg-card/5 border-white/10">
              <CardHeader><CardTitle className="text-base">AI Instructions</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>System Prompt</Label>
                  <p className="text-xs text-muted-foreground mb-1">Tell the AI who it is, how to behave, what info it has access to</p>
                  <Textarea {...register('systemPrompt')} className="bg-card/5 border-white/10" rows={5} placeholder="You are a helpful customer service agent for [Company]. You help with appointment scheduling, billing questions, and general inquiries..." />
                </div>
                <div>
                  <Label>FAQs</Label>
                  <p className="text-xs text-muted-foreground mb-1">Common questions and answers the AI should know</p>
                  <Textarea {...register('faqs')} className="bg-card/5 border-white/10" rows={4} placeholder="Q: What are your hours?&#10;A: We are open Monday-Friday 9am-5pm..." />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card/5 border-white/10">
              <CardHeader><CardTitle className="text-base">Escalation & Fallback</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Transfer to Human Conditions</Label>
                  <Textarea {...register('transferConditions')} className="bg-card/5 border-white/10" rows={2} placeholder="e.g. When caller asks for manager, billing dispute, emergency..." />
                </div>
                <div>
                  <Label>Fallback Script</Label>
                  <Textarea {...register('fallbackScript')} className="bg-card/5 border-white/10" rows={2} placeholder="What the AI says when it can't help" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Fallback Department</Label><Input {...register('fallbackDepartment')} className="bg-card/5 border-white/10 mt-1" placeholder="Support" /></div>
                  <div><Label>Max Retries</Label><Input type="number" {...register('maxRetries')} className="bg-card/5 border-white/10 mt-1 w-24" /></div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Call Summary</Label>
                    <p className="text-xs text-muted-foreground">Generate a summary after each AI-handled call</p>
                  </div>
                  <Switch checked={!!watch('callSummaryEnabled')} onCheckedChange={v => setValue('callSummaryEnabled', v)} />
                </div>
              </CardContent>
            </Card>
          </>
        )}

        <Button type="submit" disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium">
          {saving ? 'Saving...' : 'Save AI Config'}
        </Button>
      </form>
    </div>
  );
}
